import { useEffect, useState, useCallback } from "react";
import { Timer, Plus, X, Search, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { apiErrorMessage } from "@/lib/api";
import { DataTable } from "@/components/common/DataTable";
import { PageHeader, TenantPicker, useTenantScope, SelectTenantNotice, LEGAL_BASES } from "./shared";

export default function RetentionPage() {
  const scope = useTenantScope();
  const [policies, setPolicies] = useState([]);
  const [scan, setScan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ dataCategory: "", collection: "", retentionDays: 365, legalBasis: "legitimate_interest" });

  const load = useCallback(async () => {
    setLoading(true);
    try { setPolicies((await api.get("/v1/compliance/retention-policies", { params: scope.params })).data.data.items); }
    catch { setPolicies([]); }
    finally { setLoading(false); }
  }, [scope.params]);
  useEffect(() => { load(); }, [load]);

  const runScan = async () => {
    try { setScan((await api.get("/v1/compliance/retention/scan", { params: scope.params })).data.data); }
    catch (e) { toast.error(apiErrorMessage(e.response?.data?.detail)); }
  };

  const create = async () => {
    if (!form.dataCategory.trim() || !form.collection.trim()) { toast.error("Category and collection required"); return; }
    try {
      await api.post("/v1/compliance/retention-policies", { ...form, retentionDays: Number(form.retentionDays), ...scope.params });
      toast.success("Policy created");
      setShowForm(false);
      setForm({ dataCategory: "", collection: "", retentionDays: 365, legalBasis: "legitimate_interest" });
      load();
    } catch (e) { toast.error(apiErrorMessage(e.response?.data?.detail)); }
  };

  const remove = async (id) => {
    try { await api.delete(`/v1/compliance/retention-policies/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(apiErrorMessage(e.response?.data?.detail)); }
  };

  const needTenant = scope.isSuper && !scope.tenantId;

  return (
    <div data-testid="retention-page">
      <PageHeader icon={Timer} title="Data Retention"
        subtitle="Report-only — flags records past their retention window for review">
        <TenantPicker {...scope} />
        <button onClick={runScan} disabled={needTenant} className="inline-flex items-center gap-2 rounded-lg border border-border text-sm px-3 py-1.5 disabled:opacity-40" data-testid="run-scan-btn">
          <Search className="h-4 w-4" /> Run scan
        </button>
        <button onClick={() => setShowForm(true)} className="inline-flex items-center gap-2 rounded-lg bg-teal-600 hover:bg-teal-700 text-white text-sm px-3 py-1.5" data-testid="new-policy-btn">
          <Plus className="h-4 w-4" /> New policy
        </button>
      </PageHeader>

      {needTenant ? <SelectTenantNotice msg="Choose a tenant to view its retention policies." /> : (
        <>
          {scan && (
            <div className="bg-card rounded-xl border border-border p-4 mb-5" data-testid="scan-results">
              <div className="text-sm font-semibold text-foreground mb-2">
                Scan result · <span className={scan.totalExpired ? "text-amber-600" : "text-emerald-600"}>{scan.totalExpired} records past retention</span>
              </div>
              <div className="flex flex-wrap gap-2">
                {scan.items.map((r) => (
                  <span key={r.policyId} className="inline-flex items-center gap-2 rounded-lg border border-border px-2.5 py-1 text-xs">
                    {r.dataCategory} <span className={`font-semibold ${r.expiredCount ? "text-amber-600" : "text-muted-foreground"}`}>{r.expiredCount}</span>
                  </span>
                ))}
              </div>
            </div>
          )}

          <DataTable testid="policies-table" rows={policies} loading={loading}
            columns={[
              { key: "dataCategory", header: "Category" },
              { key: "collection", header: "Collection", render: (p) => <span className="font-mono text-xs">{p.collection}</span> },
              { key: "retentionDays", header: "Retention", render: (p) => `${p.retentionDays} days` },
              { key: "legalBasis", header: "Legal basis", render: (p) => <span className="capitalize text-xs">{(p.legalBasis || "").replace("_", " ")}</span> },
              { key: "enabled", header: "Enabled", render: (p) => (p.enabled ? "Yes" : "No") },
              { key: "actions", header: "", render: (p) => <button onClick={() => remove(p.id)} className="text-red-500 hover:text-red-600" data-testid={`del-policy-${p.id}`}><Trash2 className="h-4 w-4" /></button> },
            ]} />
        </>
      )}

      {showForm && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="new-policy-modal">
          <div className="bg-card rounded-xl border border-border w-full max-w-md p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-semibold text-foreground">New retention policy</h3>
              <button onClick={() => setShowForm(false)}><X className="h-5 w-5 text-muted-foreground" /></button>
            </div>
            <div className="space-y-3">
              <input placeholder="Data category" value={form.dataCategory}
                onChange={(e) => setForm({ ...form, dataCategory: e.target.value })}
                className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm" data-testid="policy-category-input" />
              <input placeholder="Collection (e.g. crm_leads)" value={form.collection}
                onChange={(e) => setForm({ ...form, collection: e.target.value })}
                className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm" data-testid="policy-collection-input" />
              <input type="number" placeholder="Retention days" value={form.retentionDays}
                onChange={(e) => setForm({ ...form, retentionDays: e.target.value })}
                className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm" data-testid="policy-days-input" />
              <select value={form.legalBasis} onChange={(e) => setForm({ ...form, legalBasis: e.target.value })}
                className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm">
                {LEGAL_BASES.map((b) => <option key={b} value={b}>{b.replace("_", " ")}</option>)}
              </select>
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <button onClick={() => setShowForm(false)} className="rounded-lg border border-border px-3 py-1.5 text-sm">Cancel</button>
              <button onClick={create} className="rounded-lg bg-teal-600 hover:bg-teal-700 text-white px-3 py-1.5 text-sm" data-testid="submit-policy-btn">Create</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
